import React, { useState, useRef, useEffect } from 'react';
import { BsChevronDown, BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import {
  MONTHS_SHORT,
  getMonthPeriod,
  getYearPeriod,
  getCurrentQuarterPeriod,
} from '../../../../core/utils/financialPeriod';

const monthKey = (year, monthIdx) => `${year}-${String(monthIdx + 1).padStart(2, '0')}`;
const brDate = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR') : '—');

const DashboardPeriodSelector = ({ selectedPeriod, onChange }) => {
  const currentYear = new Date().getFullYear();
  const [open, setOpen] = useState(false);
  const [panelYear, setPanelYear] = useState(currentYear);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const selFrom = selectedPeriod?.range?.from || '';
  const selYear = selFrom ? Number(selFrom.slice(0, 4)) : currentYear;
  const selMonthIdx = selFrom ? Number(selFrom.slice(5, 7)) - 1 : new Date().getMonth();

  const togglePanel = () => {
    if (!open) setPanelYear(selYear || currentYear);
    setOpen(!open);
  };

  const stepMonth = (delta) => {
    const d = new Date(selYear, selMonthIdx + delta, 1);
    onChange(getMonthPeriod(d.getFullYear(), d.getMonth()));
  };

  const selectOption = (option) => {
    onChange(option);
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    const from = customFrom <= customTo ? customFrom : customTo;
    const to = customFrom <= customTo ? customTo : customFrom;
    onChange({ label: `${brDate(from)} – ${brDate(to)}`, range: { from, to } });
    setOpen(false);
  };

  return (
    <div className="dg-period" ref={ref}>
      <button type="button" className="dg-period-arrow" title="Mês anterior" onClick={() => stepMonth(-1)}>
        <BsChevronLeft />
      </button>
      <button className="dg-period-btn" type="button" aria-expanded={open} aria-haspopup="true" onClick={togglePanel}>
        <span>{selectedPeriod?.label}</span>
        <BsChevronDown />
      </button>
      <button type="button" className="dg-period-arrow" title="Próximo mês" onClick={() => stepMonth(1)}>
        <BsChevronRight />
      </button>

      {open && (
        <div className="dg-period-panel">
          <div className="dg-period-year">
            <button type="button" onClick={() => setPanelYear((y) => y - 1)}>
              <BsChevronLeft />
            </button>
            <span>{panelYear}</span>
            <button type="button" onClick={() => setPanelYear((y) => y + 1)}>
              <BsChevronRight />
            </button>
          </div>

          <div className="dg-period-grid">
            {MONTHS_SHORT.map((label, idx) => {
              const key = monthKey(panelYear, idx);
              const isActive = selFrom.slice(0, 7) === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`dg-month${isActive ? ' active' : ''}`}
                  onClick={() => selectOption(getMonthPeriod(panelYear, idx))}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="dg-period-presets">
            <button type="button" onClick={() => selectOption(getYearPeriod(panelYear))}>
              Ano {panelYear} completo
            </button>
            <button type="button" onClick={() => selectOption(getCurrentQuarterPeriod())}>
              Trimestre atual
            </button>
          </div>

          <div className="dg-period-custom">
            <div className="dg-period-custom-title">Período específico</div>
            <div className="dg-period-custom-inputs">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <span>até</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
            <button
              type="button"
              className="dg-period-apply"
              disabled={!customFrom || !customTo}
              onClick={applyCustom}
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPeriodSelector;
